import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import axiosInstance from '../../config/axiosInstance';
import { toast } from 'react-toastify';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import TypingIndicator from './TypingIndicator';

const AdminChatWindow = ({ selectedChat, onChatUpdate }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const messagesContainerRef = useRef(null);
  const shouldFollowBottomRef = useRef(true);

  const mergeMessages = (current, incoming) => {
    const incomingById = new Map(
      incoming.filter((message) => message?.id).map((message) => [message.id, message]),
    );
    const merged = current.map((message) =>
      message?.id && incomingById.has(message.id)
        ? { ...message, ...incomingById.get(message.id) }
        : message,
    );
    const existingIds = new Set(merged.map((message) => message?.id).filter(Boolean));
    incoming.forEach((message) => {
      if (!message?.id || !existingIds.has(message.id)) {
        merged.push(message);
        if (message?.id) existingIds.add(message.id);
      }
    });
    return merged;
  };

  const appendMessage = (message) => {
    if (!message) return;
    shouldFollowBottomRef.current = true;
    setMessages((current) => mergeMessages(current, [message]));
  };

  const trackScrollPosition = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    shouldFollowBottomRef.current =
      container.scrollHeight - container.scrollTop - container.clientHeight < 80;
  };

  useEffect(() => {
    const chatId = selectedChat?.id;
    if (!chatId) {
      setMessages([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    shouldFollowBottomRef.current = true;
    setMessages([]);
    setLoading(true);

    const fetchMessages = async (initialLoad) => {
      try {
        const response = await axiosInstance.get(`/api/chat/${chatId}/messages`);
        if (cancelled || !response.data.success) return;
        const incoming = response.data.data.messages || [];
        setMessages((current) => initialLoad ? incoming : mergeMessages(current, incoming));
        if (onChatUpdate) onChatUpdate();
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching messages:', error);
          if (initialLoad) toast.error('Failed to load messages');
        }
      } finally {
        if (initialLoad && !cancelled) setLoading(false);
      }
    };

    fetchMessages(true);
    const interval = setInterval(() => fetchMessages(false), 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // Polling is tied only to conversation identity, not refreshed room objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.id]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (container && shouldFollowBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (messageText) => {
    if (!selectedChat) return;
    if (!messageText.trim()) return;

    try {
      const response = await axiosInstance.post(
        `/api/chat/${selectedChat.id}/message`,
        { message: messageText },
      );

      if (response.data.success) {
        const newMessage = response.data.data;
        appendMessage(newMessage);
        if (onChatUpdate) onChatUpdate();
        toast.success('Message sent successfully');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleSendImage = async (file) => {
    if (!selectedChat) return;
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      // API expects key name: image
      formData.append('image', file);

      const response = await axiosInstance.post(
        `/api/chat/${selectedChat.id}/send-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const pct = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1),
            );
            setUploadProgress(pct);
          },
        },
      );

      if (response.data?.success) {
        const newMessage = response.data?.data;
        appendMessage(newMessage);
        if (onChatUpdate) onChatUpdate();
        toast.success('Image sent successfully');
      } else {
        toast.error(response.data?.message || 'Failed to send image');
      }
    } catch (error) {
      console.error('Error sending image:', error);
      toast.error(error.response?.data?.message || 'Failed to send image');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSendVideo = async (file) => {
    if (!selectedChat) return;
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('video', file);

      const response = await axiosInstance.post(
        `/api/chat/${selectedChat.id}/send-video`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const pct = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1),
            );
            setUploadProgress(pct);
          },
        },
      );

      if (response.data?.success) {
        const newMessage = response.data?.data;
        appendMessage(newMessage);
        if (onChatUpdate) onChatUpdate();
        toast.success('Video sent successfully');
      } else {
        toast.error(response.data?.message || 'Failed to send video');
      }
    } catch (error) {
      console.error('Error sending video:', error);
      toast.error(error.response?.data?.message || 'Failed to send video');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  if (!selectedChat) {
    return (
      <div
        className='card border-0 shadow-sm d-flex align-items-center justify-content-center'
        style={{ height: '100%', minHeight: 0 }}
      >
        <div className='text-center text-muted'>
          <i className='bi bi-chat-left-text' style={{ fontSize: '4rem' }}></i>
          <h5 className='mt-3'>Select a chat to start</h5>
          <p>Choose a conversation from the list to view messages</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='card border-0 shadow-sm'
      style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {/* Chat Header */}
      <div
        className='card-header bg-white border-bottom py-3'
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <div className='d-flex justify-content-between align-items-center'>
          <div className='d-flex align-items-center gap-3'>
            <div>
              <h6 className='mb-0 fw-semibold text-white'>
                {selectedChat.user?.fullName || 'Guest'}
              </h6>
              <small className='text-white-50'>
                {selectedChat.user
                  ? `UID: ${selectedChat.user.uid} | ${selectedChat.user.email}`
                  : `Guest reference: ${selectedChat.guestReference || 'Unavailable'}`}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        onScroll={trackScrollPosition}
        className='card-body'
        style={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: '#f8f9fa',
        }}
      >
        {loading ? (
          <div className='text-center py-5'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className='text-center py-5 text-muted'>
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <ChatMessage
                key={message.id || index}
                message={message}
                isAdmin={message.isFromAdmin}
              />
            ))}
            {typing && <TypingIndicator userName={selectedChat.userName} />}
          </>
        )}
      </div>

      {/* Chat Input */}
      <div className='card-footer bg-white border-top py-3'>
        <ChatInput
          onSendMessage={handleSendMessage}
          onSendImage={handleSendImage}
          onSendVideo={handleSendVideo}
          disabled={selectedChat.status === 'CLOSED'}
          uploadProgress={uploadProgress}
          isUploading={isUploading}
          placeholder={
            selectedChat.status === 'CLOSED'
              ? 'This chat is closed'
              : 'Type your message...'
          }
        />
      </div>
    </div>
  );
};

export default AdminChatWindow;
